import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, apiClient } from '../App';
import { Truck, MapPin, DollarSign, Briefcase, Filter, Search, ArrowLeft, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export default function DriverJobBoard() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLicense, setFilterLicense] = useState('all');

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const res = await apiClient.get('/drivers/jobs');
      setJobs(res.data.filter(j => j.is_active));
    } catch (error) {
      console.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (job.company_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLicense = filterLicense === 'all' || job.license_required === filterLicense;
    return matchesSearch && matchesLicense;
  });

  const getLicenseBadge = (license) => {
    const isCDL = license?.includes('CDL');
    return isCDL ? 'license-badge-cdl' : '';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Truck className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-slate-900">True Choice Enterprise</span>
            </Link>
            <div className="flex items-center gap-4">
              {user ? (
                <Link to="/dashboard">
                  <Button className="btn-accent" data-testid="dashboard-link">Dashboard</Button>
                </Link>
              ) : (
                <Link to="/auth?role=driver">
                  <Button className="btn-accent" data-testid="login-link">Join as Driver</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
          <h1 className="text-3xl font-bold">Driver Job Board</h1>
          <p className="text-slate-300 mt-2">Find CDL and non-CDL driving opportunities with verified carriers</p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by title, company, location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filterLicense} onValueChange={setFilterLicense}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-select">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="License Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Licenses</SelectItem>
                <SelectItem value="CDL-A">CDL-A</SelectItem>
                <SelectItem value="CDL-B">CDL-B</SelectItem>
                <SelectItem value="CDL-C">CDL-C</SelectItem>
                <SelectItem value="Non-CDL">Non-CDL</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Job Listings */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <Briefcase className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No jobs found</h3>
            <p className="text-slate-500">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <div key={job.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow" data-testid={`driver-job-${job.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-slate-900 text-lg">{job.title}</h3>
                      <span className={`license-badge ${getLicenseBadge(job.license_required)}`}>
                        {job.license_required}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-slate-600 mb-2">
                      <Building className="h-4 w-4" />
                      <span>{job.company_name || job.carrier_name}</span>
                    </div>
                    
                    <p className="text-slate-500 text-sm mb-4">{job.description}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4 text-green-500" />
                        {job.salary_range}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-blue-500" />
                        {job.location}
                      </span>
                      <span className="badge bg-slate-100 text-slate-600">
                        {job.job_type}
                      </span>
                      <span className="text-slate-400">
                        {job.applicant_count} applicants
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0">
                    {user?.role === 'driver' ? (
                      <Link to="/driver/dashboard">
                        <Button className="btn-accent" data-testid={`apply-${job.id}`}>
                          Apply Now
                        </Button>
                      </Link>
                    ) : (
                      <Link to="/auth?role=driver">
                        <Button className="btn-secondary">
                          Sign in to Apply
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
